import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import reportApi from "../../../../api/reportApi";
import pipelineStageApi from "../../../../api/pipelineStageApi";
import { useReportFilters } from "../../contexts/ReportFiltersContext";
import { formatNumber } from "../../utils/format";
import EmptyState from "../../components/EmptyState";
import HeroKpiStrip from "./components/HeroKpiStrip";
import ReportLibraryGrid from "./components/ReportLibraryGrid";

const ReportsHubPage = () => {
  const { shopId, from, to, isRangeValid } = useReportFilters();
  const auth = useSelector((s) => s.auth);
  const accountId = auth?.user?.accountId;

  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stagesCount, setStagesCount] = useState(null);

  // Overview KPIs — scoped to shop + date range.
  useEffect(() => {
    if (!shopId || !isRangeValid) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    reportApi
      .getOverview({ shopId, from, to })
      .then((res) => {
        if (cancelled) return;
        if (res.status === 1) setOverview(res.data);
        else setError(res.message || "Không tải được dữ liệu");
      })
      .catch((err) => !cancelled && setError(err.message))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [shopId, from, to, isRangeValid]);

  // Pipeline stages count — scoped to account, independent of date range.
  useEffect(() => {
    if (!accountId) return;
    let cancelled = false;
    pipelineStageApi
      .getByAccountId(accountId)
      .then((res) => {
        if (cancelled) return;
        if (res?.status === 1) {
          setStagesCount(Array.isArray(res.data) ? res.data.length : 0);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [accountId]);

  const hints = useMemo(() => {
    const h = {};
    if (overview) {
      const newConv = Number(overview.newConversations || 0);
      const unresolved = Number(overview.unresolvedCount || 0);
      h.activity = `${formatNumber(
        (overview.customerMessages || 0) + (overview.adminMessages || 0),
      )} tin trong kỳ`;
      h.staff = `${formatNumber(newConv)} hội thoại`;
      h.conversations =
        unresolved > 0 ? `${formatNumber(unresolved)} đang kẹt` : "Không có kẹt";
      h.heatmap = "Giờ cao điểm";
    }
    if (stagesCount !== null) {
      h.pipeline =
        stagesCount > 0 ? `${stagesCount} giai đoạn` : "Chưa có giai đoạn";
    }
    return h;
  }, [overview, stagesCount]);

  if (!shopId) {
    return (
      <div className="p-6">
        <EmptyState title="Chọn Zalo để xem báo cáo" />
      </div>
    );
  }

  return (
    <div className="p-4 space-y-6 max-w-7xl">
      {error && (
        <div className="p-3 bg-red-50 text-red-700 rounded text-sm">{error}</div>
      )}

      <section>
        <div className="mb-3">
          <h2 className="text-base font-semibold text-gray-900">
            Tổng quan nhanh
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Số liệu chính trong khoảng thời gian bạn đã chọn
          </p>
        </div>
        <HeroKpiStrip data={overview} loading={loading && !overview} />
      </section>

      <section>
        <div className="mb-3">
          <h2 className="text-base font-semibold text-gray-900">
            Thư viện báo cáo
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Mỗi báo cáo là một góc nhìn riêng — chọn để đi sâu
          </p>
        </div>
        <ReportLibraryGrid hints={hints} />
      </section>
    </div>
  );
};

export default ReportsHubPage;
